import { Controller } from "stimulus"
import { Modal } from "bootstrap"
import { hasLanguageCookie, setLanguageCookie, releaseKlaro } from "../utils/language_gate"
import { isCrawler } from "../utils/is_crawler"

export default class extends Controller {
  static targets = ['i18n', 'title', 'intro', 'close']

  connect() {
    // The same controller is attached to the root interstitial, which is a full page and not
    // a dialog. There, only choose() is relevant.
    this.isModal = this.element.classList.contains('modal')
    if (!this.isModal) {
      return
    }

    if (hasLanguageCookie()) {
      releaseKlaro()
      return
    }

    this.applyBrowserLanguage()

    // Googlebot renders JavaScript, so it does open this dialog. The content stays in the
    // DOM either way, but marking <main> non-interactive and dropping it from the
    // accessibility tree has no upside in a crawler's rendered snapshot.
    this.onShown = () => {
      if (!isCrawler()) {
        this.setBackgroundInert(true)
      }
    }
    this.onHidden = () => this.handleHidden()
    this.element.addEventListener('shown.bs.modal', this.onShown)
    this.element.addEventListener('hidden.bs.modal', this.onHidden)

    this.modal = Modal.getOrCreateInstance(this.element)
    this.modal.show()
  }

  disconnect() {
    if (!this.isModal) {
      return
    }
    this.element.removeEventListener('shown.bs.modal', this.onShown)
    this.element.removeEventListener('hidden.bs.modal', this.onHidden)
    this.setBackgroundInert(false)
  }

  // Covers every route out of the dialog: close button, ESC and backdrop click.
  handleHidden() {
    this.setBackgroundInert(false)
    if (!hasLanguageCookie()) {
      // Dismissing is itself a choice - the visitor is staying in the language they are
      // already in - so record that rather than a sentinel value.
      setLanguageCookie(this.currentLanguage())
    }
    this.restoreFocus()
    releaseKlaro()
  }

  choose(event) {
    setLanguageCookie(languageOfLink(event.currentTarget))
    // Navigation proceeds normally. document.cookie is synchronous, so the cookie is written
    // before the request starts; no preventDefault needed.
  }

  dismiss() {
    if (this.modal) {
      this.modal.hide()
    }
  }

  // Bootstrap only restores focus when a data-bs-toggle trigger opened the modal. This one
  // opens itself, so without this focus would fall to <body> and a screen reader's virtual
  // cursor would jump back to the top of the document.
  restoreFocus() {
    const target = document.querySelector('#languageDropdown') || document.querySelector('#main')
    if (target) {
      target.focus()
    }
  }

  // Everything outside the dialog, rather than a fixed list of landmarks - the page also
  // carries skip links, noscript iframes and the service modal as body children.
  setBackgroundInert(on) {
    Array.from(document.body.children).forEach(el => {
      if (el !== this.element) {
        el.toggleAttribute('inert', on)
      }
    })
  }

  // The page is cached, so its language is whatever URL the visitor happened to land on -
  // which for a country chooser is very likely the wrong one. Every translation is rendered
  // into the markup; pick the one matching the browser and swap it in before .show(), so
  // there is no visible flash.
  applyBrowserLanguage() {
    if (!this.hasI18nTarget) {
      return
    }

    let dictionary
    try {
      dictionary = JSON.parse(this.i18nTarget.textContent)
    } catch (e) {
      return
    }

    const language = pickLanguage(Object.keys(dictionary))
    const labels = dictionary[language]
    if (!labels) {
      return
    }

    // Without this the dialog's chrome would be read out with the phonemes of the page
    // language - the same problem the per-country lang attributes solve. The country links
    // carry their own lang, so they still win over this.
    this.element.setAttribute('lang', language)

    this.setLabel('title', labels.title)
    this.setLabel('intro', labels.intro)

    // The close button has no text content; its accessible name is the aria-label.
    if (this.hasCloseTarget && labels.close) {
      this.closeTarget.setAttribute('aria-label', labels.close)
    }
  }

  currentLanguage() {
    return pathLanguage(window.location.pathname) || 'unknown'
  }

  setLabel(name, value) {
    const capitalized = name.charAt(0).toUpperCase() + name.slice(1)
    if (value && this['has' + capitalized + 'Target']) {
      this[name + 'Target'].textContent = value
    }
  }
}

// The cookie value is a language URL prefix - 'de-at', 'fr-ch', 'es'. LanguageDetectionHandler
// matches it against each site language's base path to redirect the site root, so it has to be
// derived from URLs and not from hreflang: es and it-ch have an empty hreflang in
// config/sites/windhager/config.yaml.
function pathLanguage(pathname) {
  return String(pathname || '').split('/')[1] || ''
}

function languageOfLink(link) {
  let prefix = ''
  try {
    const url = new URL(link.href, window.location.origin)
    if (url.origin === window.location.origin) {
      prefix = pathLanguage(url.pathname)
    }
  } catch (e) {
    // malformed href - fall through to the hreflang below
  }
  // An external destination has no prefix of ours. Record its hreflang so the value still says
  // something; it will not match a site language, so the root will keep offering the chooser.
  return prefix || link.getAttribute('hreflang') || 'external'
}

// Match the browser's preferred languages against what we actually shipped, on the base
// subtag, so de-CH and de-DE both resolve to 'de'. Falls back to English.
function pickLanguage(available) {
  const preferred = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language]

  for (const entry of preferred) {
    if (!entry) {
      continue
    }
    const base = String(entry).toLowerCase().split('-')[0]
    if (available.indexOf(base) !== -1) {
      return base
    }
  }

  return available.indexOf('en') !== -1 ? 'en' : available[0]
}
